import { isValidDateInput, parseDate } from '../../utils';
import { isDuration, parseDuration, type Duration } from './duration';

export enum DateTimeDurationPickerValueType {
  DateTime = 'DateTime',
  Duration = 'Duration',
}

export type DateTimeDurationPickerValue =
  | {
      type: DateTimeDurationPickerValueType.DateTime;
      value: Date | null;
    }
  | {
      type: DateTimeDurationPickerValueType.Duration;
      value: Duration;
    };

export type DateTimeDurationPickerControlValue = Duration | Date | null;

export type DateTimeDurationPickerEntryModes = readonly [
  DateTimeDurationPickerValueType,
  ...DateTimeDurationPickerValueType[],
];

export const DEFAULT_ENTRY_MODES: DateTimeDurationPickerEntryModes = [
  DateTimeDurationPickerValueType.DateTime,
  DateTimeDurationPickerValueType.Duration,
];

/**
 * Returns a non-empty entry mode tuple at runtime.
 * Falls back to `DEFAULT_ENTRY_MODES` when input is missing or empty.
 */
export function getSafeEntryModes(
  entryModes?: readonly DateTimeDurationPickerValueType[]
): DateTimeDurationPickerEntryModes {
  if (entryModes?.[0] != null) {
    return entryModes as DateTimeDurationPickerEntryModes;
  }
  return DEFAULT_ENTRY_MODES;
}

export function isDateTimeDurationPickerValue(value: any): value is DateTimeDurationPickerValue {
  return (
    value?.type === DateTimeDurationPickerValueType.DateTime || value?.type === DateTimeDurationPickerValueType.Duration
  );
}

export type ResolvedIncomingValueState =
  | {
      kind: 'duration';
      duration: Duration;
    }
  | {
      kind: 'dateTime';
      dateTime: Date;
    }
  | {
      kind: 'sanitize';
      fallbackEntryMode: DateTimeDurationPickerValueType | null;
      isUnsupportedControlledValue: boolean;
    };

interface IncomingValueContext {
  value: DateTimeDurationPickerControlValue;
  hasDateTimeEntryMode: boolean;
  hasDurationEntryMode: boolean;
}

export function resolveIncomingValueState({
  value,
  hasDateTimeEntryMode,
  hasDurationEntryMode,
}: IncomingValueContext): ResolvedIncomingValueState {
  // Duration input is valid only when Duration mode is allowed.
  if (isDuration(value)) {
    if (hasDurationEntryMode) {
      return { kind: 'duration', duration: value };
    }
    // Duration value supplied to DateTime-only picker: sanitize and keep a valid mode.
    return {
      kind: 'sanitize',
      fallbackEntryMode: hasDateTimeEntryMode ? DateTimeDurationPickerValueType.DateTime : null,
      isUnsupportedControlledValue: true,
    };
  }

  // Date/Date-like input is valid only when DateTime mode is allowed.
  if (value != null && isValidDateInput(value)) {
    if (hasDateTimeEntryMode) {
      return { kind: 'dateTime', dateTime: parseDate(value) };
    }
    // Date value supplied to Duration-only picker: sanitize and keep a valid mode.
    return {
      kind: 'sanitize',
      fallbackEntryMode: hasDurationEntryMode ? DateTimeDurationPickerValueType.Duration : null,
      isUnsupportedControlledValue: true,
    };
  }

  // Null/empty/invalid values should clear UI while preserving whichever mode is allowed.
  if (!hasDateTimeEntryMode && hasDurationEntryMode) {
    return {
      kind: 'sanitize',
      fallbackEntryMode: DateTimeDurationPickerValueType.Duration,
      isUnsupportedControlledValue: false,
    };
  }
  if (hasDateTimeEntryMode && !hasDurationEntryMode) {
    return {
      kind: 'sanitize',
      fallbackEntryMode: DateTimeDurationPickerValueType.DateTime,
      isUnsupportedControlledValue: false,
    };
  }

  return { kind: 'sanitize', fallbackEntryMode: null, isUnsupportedControlledValue: false };
}

export function getIncomingValueStateSignature({
  value,
  hasDateTimeEntryMode,
  hasDurationEntryMode,
}: IncomingValueContext): string {
  const modeSignature = `dt:${hasDateTimeEntryMode ? 1 : 0}|du:${hasDurationEntryMode ? 1 : 0}`;

  if (isDuration(value)) {
    const normalized = parseDuration(value);
    return `duration:${normalized.days}:${normalized.hours}:${normalized.minutes}:${normalized.seconds}|${modeSignature}`;
  }

  if (value != null && isValidDateInput(value)) {
    return `dateTime:${parseDate(value).getTime()}|${modeSignature}`;
  }

  return `empty|${modeSignature}`;
}

export function getInitialEntryMode({
  initialEntryMode,
  value,
  entryModes,
}: {
  initialEntryMode: DateTimeDurationPickerValueType | undefined;
  value: DateTimeDurationPickerControlValue;
  entryModes: DateTimeDurationPickerEntryModes;
}): DateTimeDurationPickerValueType {
  const defaultEntryMode = entryModes[0];

  // 1. Respect explicit initialEntryMode only when no value is currently set.
  if (initialEntryMode && value == null && entryModes.includes(initialEntryMode)) {
    return initialEntryMode;
  }

  // 2. Prefer the mode that matches the current value type, if available.
  if (isDuration(value)) {
    return entryModes.includes(DateTimeDurationPickerValueType.Duration)
      ? DateTimeDurationPickerValueType.Duration
      : defaultEntryMode;
  }

  // Date-like values should initialize in DateTime mode when allowed.
  // If DateTime is not available (for example, Duration-only config), we
  // still return a concrete allowed mode to keep panel state and emit logic aligned.
  if (value != null && isValidDateInput(value)) {
    return entryModes.includes(DateTimeDurationPickerValueType.DateTime)
      ? DateTimeDurationPickerValueType.DateTime
      : defaultEntryMode;
  }

  // 3. Fallback to the first available mode.
  return defaultEntryMode;
}
